import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, CheckCircle, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function QRScanner({ onScan, onClose }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanning = async () => {
    setError(null);
    setScanning(true);

    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          scanner.stop().catch(() => {});
          setScanning(false);
          onScan(decodedText);
        },
        () => {}
      );
    } catch (err) {
      setError("Camera access denied. Please allow camera permissions.");
      setScanning(false);
    }
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
    }
    setScanning(false);
  };

  return (
    <div className="flex flex-col items-center gap-4" data-testid="qr-scanner">
      <div
        id="qr-reader"
        ref={containerRef}
        className="w-full max-w-sm rounded-lg overflow-hidden bg-black"
        style={{ minHeight: scanning ? 300 : 0 }}
      />

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="qr-scanner-error">
          <Warning className="w-4 h-4" weight="fill" />
          {error}
        </div>
      )}

      <div className="flex gap-3">
        {!scanning ? (
          <Button
            onClick={startScanning}
            className="bg-bch-teal hover:bg-bch-teal-dark text-white"
            data-testid="qr-start-scan-btn"
          >
            <Camera className="w-5 h-5 mr-2" weight="duotone" />
            Start Camera
          </Button>
        ) : (
          <Button
            onClick={stopScanning}
            variant="ghost"
            className="text-red-400 hover:bg-red-500/10"
            data-testid="qr-stop-scan-btn"
          >
            <X className="w-5 h-5 mr-2" />
            Stop Camera
          </Button>
        )}

        <Button onClick={onClose} variant="ghost" className="text-secondary" data-testid="qr-close-btn">
          Cancel
        </Button>
      </div>

      <p className="text-muted text-xs text-center max-w-xs">
        Point your camera at the meeting QR code displayed by the admin.
      </p>
    </div>
  );
}
